import { createAction } from "@reduxjs/toolkit";
import { initialState as commonInitialState } from "../../utils/redux/commonActionHandlers";
import { withResolvedLabels } from "../../utils/redux/resolveFilterLabels";
import { RESOLVABLE_TYPES } from "./urlFilters";
import getVacanciesSagas from "./sagas";

const extendedConstants = (baseConstants) => ({
  ...baseConstants,
  SET_FILTER: "@VACANCIES/SET_FILTER",
  SET_FILTER_LABELS: "@VACANCIES/SET_FILTER_LABELS",
  PIN_VACANCIES: "@VACANCIES/PIN_VACANCIES",
  PATCH_ITEM: "@VACANCIES/PATCH_ITEM",
});

const extendedActions = (baseActions, constants) => ({
  ...baseActions,
  setFilter: createAction(constants.SET_FILTER),
  setFilterLabels: createAction(constants.SET_FILTER_LABELS),
  pinVacancies: createAction(constants.PIN_VACANCIES),
  patchItem: createAction(constants.PATCH_ITEM),
});

const extendedInitialState = {
  ...commonInitialState,
  // CREW-1233 — the list has always been ordered by joining date, but the
  // default was patched into `Api.vacancies.index` instead of living here. The
  // header's active-sort arrow is `column.id === sort.field`, so with an empty
  // field it showed nothing and the first click on "Rank · Start" re-sent the
  // order already on screen rather than reversing it. Seeding it fixes both.
  sort: { field: "start", direction: "asc" },
  // Same as /vessels: the search box belongs in the URL, not in localStorage —
  // see utils/redux/urlFilterSagas for why the old reasoning was backwards.
  search: "",
  // Ids kept visible past the "open positions only" scope of the list — the
  // row a recruiter just approved must not vanish from under them. Memory
  // only, deliberately NOT in the URL: a reload starts clean (the row is gone
  // for good, as it should be), and a shared link never carries somebody
  // else's pins (the CREW-1169 URL contract stays about filters).
  pinnedIds: [],
};

const extendedActionHandlers = (baseActionHandlers, constants) => ({
  ...baseActionHandlers,
  // CREW-1169: replace the whole filter with what the URL says — draft and
  // applied in one step, since the URL is already the applied state — and
  // reset paging, because page 4 of the old filter means nothing here.
  [constants.SET_FILTER]: (state, action) => ({
    ...state,
    filter: action.payload,
    appliedFilter: action.payload,
    // A new filter is a new question: pins from the previous one stop being
    // "what I just did here" and are dropped.
    pinnedIds: [],
    pagination: {
      ...state.pagination,
      current: 1,
    },
  }),
  // CREW-1209 — names that arrived for values a URL carried as bare ids. A
  // separate action, not another SET_FILTER: this must not reset paging, drop
  // pins or re-trigger the resolve saga. Labels are merged by value, so an
  // answer that arrives after the user has picked something else fills in only
  // what is still there, and never overwrites a label already on screen.
  [constants.SET_FILTER_LABELS]: (state, action) => ({
    ...state,
    filter: withResolvedLabels(state.filter, RESOLVABLE_TYPES, action.payload),
    appliedFilter: withResolvedLabels(state.appliedFilter, RESOLVABLE_TYPES, action.payload),
  }),
  // Applying a filter is the "new question" the reset above talks about, and
  // it has to say so itself. It only reached SET_FILTER by accident: the URL
  // round-trip came back label-less, so the filter always compared different
  // and SET_FILTER always fired. CREW-1208 keeps the labels, that accident
  // stops, and without this the pins would outlive the filter they belong to.
  [constants.APPLY_FILTER]: (state, action) => ({
    ...baseActionHandlers[constants.APPLY_FILTER](state, action),
    pinnedIds: [],
  }),
  [constants.PIN_VACANCIES]: (state, action) => ({
    ...state,
    pinnedIds: [...new Set([...state.pinnedIds, ...action.payload.map(String)])],
  }),
  // CREW-1205 — a vacancy changed on somebody else's screen. Merge the
  // fields the socket carries into the row we are already showing; a row
  // that is not on this page (other pagination, other filter) is not our
  // business and the action is a no-op for it.
  //
  // The rows live under `data` — that is the kit's shape
  // (utils/redux/commonActionHandlers `initialState`), not `items`.
  //
  // Only the keys present in the payload are written, so a future payload
  // that carries one field does not blank the others, and `data` keeps its
  // identity when nothing matched — a new array on every unrelated event
  // would repaint the whole table for nothing.
  [constants.PATCH_ITEM]: (state, action) => {
    const { id, ...fields } = action.payload || {};
    if (id === undefined || id === null) return state;

    const target = String(id);
    let changed = false;

    const data = (state.data || []).map((item) => {
      if (String(item.id) !== target) return item;
      changed = true;
      return { ...item, attributes: { ...item.attributes, ...fields } };
    });

    return changed ? { ...state, data } : state;
  },
});

const newSagas = (baseSagas, constants, actions) =>
  getVacanciesSagas(baseSagas, constants, actions);

// The vacancies feature owns its watcher list outright (CREW-1169): the
// generic kit fetches on APPLY_FILTER *and* on LOCATION_CHANGE, and with the
// URL as the source of truth both of those fire before the filter has been
// hydrated. `getVacanciesSagas` re-declares everything the kit does — against
// the kit's own workers — so the only behavioural difference is the filter
// path. Anything added to `getCRUDSagas` later has to be mirrored there.
const extendedSagas = (baseSagas, newSagas) => () => newSagas;

const extendedSelectors = (baseSelectors) => (state) => ({
  ...baseSelectors(state),
});

export default {
  extendedConstants,
  extendedActions,
  extendedInitialState,
  extendedActionHandlers,
  extendedSagas,
  newSagas,
  extendedSelectors,
};
